/**
 * ComparisonTabs — 7-tab strip routing the active comparison panel
 * (Overview · Details · Scoring · Financials · Risks · Pros & Cons ·
 * Map). Tab labels include short descriptions on hover; mobile collapses
 * the strip to icons only.
 *
 * Presentational only — parent owns the active key and the onChange
 * handler. Switching tabs preserves picker state because the picker
 * lives in the parent.
 *
 * Linear: MYD-216 (F1).
 */

import {
	LayoutDashboard,
	Building2,
	BarChart3,
	DollarSign,
	ShieldAlert,
	ListChecks,
	Map,
} from 'lucide-react'
import { cn } from 'lib/utils'

export type ComparisonTabKey =
	| 'overview'
	| 'details'
	| 'scoring'
	| 'financials'
	| 'risks'
	| 'proscons'
	| 'map'

export const COMPARISON_TABS: {
	key: ComparisonTabKey
	label: string
	icon: typeof LayoutDashboard
	description: string
}[] = [
	{
		key: 'overview',
		label: 'Overview',
		icon: LayoutDashboard,
		description: 'Headline metrics per site',
	},
	{ key: 'details', label: 'Details', icon: Building2, description: 'Full property metadata' },
	{ key: 'scoring', label: 'Scoring', icon: BarChart3, description: 'Weighted scores + delta' },
	{
		key: 'financials',
		label: 'Financials',
		icon: DollarSign,
		description: 'NPV, IRR, payback, yield',
	},
	{ key: 'risks', label: 'Risks', icon: ShieldAlert, description: 'Risk counts by severity' },
	{
		key: 'proscons',
		label: 'Pros & Cons',
		icon: ListChecks,
		description: 'Per-site pros / cons',
	},
	{ key: 'map', label: 'Map', icon: Map, description: 'Sites + rings on one map' },
]

interface ComparisonTabsProps {
	active: ComparisonTabKey
	onChange: (key: ComparisonTabKey) => void
}

const ComparisonTabs = ({ active, onChange }: ComparisonTabsProps) => (
	<div className="border-b border-gray-200 overflow-x-auto -mx-1 px-1">
		<nav className="flex gap-1 whitespace-nowrap">
			{COMPARISON_TABS.map((tab) => {
				const Icon = tab.icon
				const isActive = active === tab.key
				return (
					<button
						key={tab.key}
						type="button"
						onClick={() => onChange(tab.key)}
						title={tab.description}
						className={cn(
							'flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors',
							isActive
								? 'border-[#0080fe] text-[#0080fe]'
								: 'border-transparent text-gray-500 hover:text-gray-700',
						)}
					>
						<Icon className="w-4 h-4" />
						<span className="hidden sm:inline">{tab.label}</span>
					</button>
				)
			})}
		</nav>
	</div>
)

export default ComparisonTabs
